import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import { cn } from '@/lib/utils';
import { type SanityImageProps, resolveAlt, resolveImageA11y, getImageDimensions, buildImageSrc, getLqip, computeDefaultSizes, parseAspectRatio } from './SanityImage.shared';

/**
 * Server-rendered SanityImage -- zero client JS.
 * Uses next/image with Sanity CDN loader for optimized delivery.
 * For interactive features (loading transition, error fallback), use the client version.
 */
export function SanityImageServer(t0) {
  const $ = _c(51);
  const {
    image,
    width: widthProp,
    height: heightProp,
    className,
    fill: fillProp,
    alt: altProp,
    priority: t1,
    sizes: sizesProp,
    aspectRatio,
    quality: t2
  } = t0;
  const priority = t1 === undefined ? false : t1;
  const quality = t2 === undefined ? 80 : t2;
  if (!image.asset?._id) {
    return null;
  }
  let alt;
  let t3;
  if ($[0] !== altProp || $[1] !== image) {
    alt = resolveAlt(altProp, image);
    t3 = resolveImageA11y(alt);
    $[0] = altProp;
    $[1] = image;
    $[2] = alt;
    $[3] = t3;
  } else {
    alt = $[2];
    t3 = $[3];
  }
  const a11yProps = t3;
  let height;
  let parsedRatio;
  let t4;
  let width;
  if ($[4] !== aspectRatio || $[5] !== heightProp || $[6] !== image || $[7] !== quality || $[8] !== widthProp) {
    const intrinsic = getImageDimensions(image);
    parsedRatio = parseAspectRatio(aspectRatio);
    width = widthProp ?? intrinsic.width;
    const effectiveRatio = parsedRatio ?? intrinsic.aspectRatio;
    height = heightProp ?? (width && effectiveRatio ? Math.round(width / effectiveRatio) : intrinsic.height);
    t4 = buildImageSrc(image, {
      width: width ?? 1200,
      height,
      quality
    });
    $[4] = aspectRatio;
    $[5] = heightProp;
    $[6] = image;
    $[7] = quality;
    $[8] = widthProp;
    $[9] = height;
    $[10] = parsedRatio;
    $[11] = t4;
    $[12] = width;
  } else {
    height = $[9];
    parsedRatio = $[10];
    t4 = $[11];
    width = $[12];
  }
  const src = t4;
  let t5;
  if ($[13] !== image) {
    t5 = getLqip(image);
    $[13] = image;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const lqip = t5;
  let t6;
  if ($[15] !== fillProp || $[16] !== sizesProp || $[17] !== width) {
    t6 = computeDefaultSizes(sizesProp, fillProp, width);
    $[15] = fillProp;
    $[16] = sizesProp;
    $[17] = width;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const sizes = t6;
  let t7;
  if ($[19] !== lqip) {
    t7 = lqip ? {
      placeholder: "blur" as const,
      blurDataURL: lqip
    } : {};
    $[19] = lqip;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const placeholderProps = t7;
  if (fillProp) {
    let t8;
    if ($[21] !== className) {
      t8 = cn("relative overflow-hidden absolute inset-0", className);
      $[21] = className;
      $[22] = t8;
    } else {
      t8 = $[22];
    }
    let t9;
    if ($[23] !== a11yProps || $[24] !== alt || $[25] !== placeholderProps || $[26] !== priority || $[27] !== quality || $[28] !== sizes || $[29] !== src) {
      t9 = <Image src={src} alt={alt} fill={true} priority={priority} sizes={sizes} quality={quality} className="object-cover" {...a11yProps} {...placeholderProps} />;
      $[23] = a11yProps;
      $[24] = alt;
      $[25] = placeholderProps;
      $[26] = priority;
      $[27] = quality;
      $[28] = sizes;
      $[29] = src;
      $[30] = t9;
    } else {
      t9 = $[30];
    }
    let t10;
    if ($[31] !== t8 || $[32] !== t9) {
      t10 = <div className={t8}>{t9}</div>;
      $[31] = t8;
      $[32] = t9;
      $[33] = t10;
    } else {
      t10 = $[33];
    }
    return t10;
  }
  const resolvedWidth = width ?? 1200;
  let t8;
  if ($[34] !== height || $[35] !== parsedRatio || $[36] !== resolvedWidth) {
    t8 = height ?? (parsedRatio ? Math.round(resolvedWidth / parsedRatio) : 800);
    $[34] = height;
    $[35] = parsedRatio;
    $[36] = resolvedWidth;
    $[37] = t8;
  } else {
    t8 = $[37];
  }
  const resolvedHeight = t8;
  let t9;
  if ($[38] !== className) {
    t9 = cn("max-w-full h-auto", className);
    $[38] = className;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  let t10;
  if ($[40] !== a11yProps || $[41] !== alt || $[42] !== placeholderProps || $[43] !== priority || $[44] !== quality || $[45] !== resolvedHeight || $[46] !== resolvedWidth || $[47] !== sizes || $[48] !== src || $[49] !== t9) {
    t10 = <Image src={src} alt={alt} width={resolvedWidth} height={resolvedHeight} priority={priority} sizes={sizes} quality={quality} className={t9} {...a11yProps} {...placeholderProps} />;
    $[40] = a11yProps;
    $[41] = alt;
    $[42] = placeholderProps;
    $[43] = priority;
    $[44] = quality;
    $[45] = resolvedHeight;
    $[46] = resolvedWidth;
    $[47] = sizes;
    $[48] = src;
    $[49] = t9;
    $[50] = t10;
  } else {
    t10 = $[50];
  }
  return t10;
}